import React, { useEffect } from "react";
import { connect, useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { useFetch } from "hooks";
import { path } from "ramda";
import moment from "moment";
import { put } from "utils/api";
import { getApiErrorMessage, formatPhone } from "utils/misc";
import { Spinner } from "components/ui";
import { errorMessage } from "actions/message";
import { setPatientBatchIds } from "actions/batch";
import { createTab } from "actions/system-tray";
import useRowSelect, { SelectRowContext } from "hooks/useRowSelectNew";
import { selectors } from "reducers";

const formatDate = date => (date ? moment(date).format("MM/DD/YYYY") : null);

const OnboardingCoachScorecardPatients = ({
  filters,
  closeModal,
  inactivationReasons
}) => {
  const dispatch = useDispatch();

  const getReasonText = reasonId => {
    return inactivationReasons.find(reason => reason.value === reasonId)?.text;
  };

  const {
    response: patientList,
    fetchData: fetchPatientList,
    isFetching: fetchingPatientList
  } = useFetch({
    apiFn: () =>
      put(
        `onboarding-dashboard/coach-scorecard/${filters.coachId}/patients/${filters.type}`,
        filters
      ),
    defaultValue: null,
    transformError: path(["response", "body", "status"]),
    onError: error => {
      dispatch(
        errorMessage(
          `Category: ${filters.type} ERROR: , ${getApiErrorMessage(error)}`
        )
      );
      closeModal();
    }
  });

  async function initialData() {
    await fetchPatientList();
  }

  useEffect(() => {
    initialData();
  }, []);

  const {
    toggleAllRows,
    isRowSelected,
    toggleRow,
    clearSelectedRows,
    selectedRows
  } = useRowSelect([], "id", {});

  return (
    <SelectRowContext.Provider
      value={{
        toggleAllRows,
        toggleRow,
        selectedRows,
        isRowSelected,
        clearSelectedRows
      }}
    >
      <div className="special-fields" style={{ height: "100%" }}>
        <header className="page-header">
          <div className="page-header-title">
            <h1>
              <span className="page-title">{`${filters.type} Patients`}</span>
            </h1>
          </div>
        </header>

        <div style={{ overflowX: "scroll", height: "93%" }}>
          <div
            style={{
              display: "grid",
              gridGap: 10,
              alignItems: "left",
              height: "fit-content"
            }}
          >
            {!fetchingPatientList ? (
              patientList && patientList.length > 0 ? (
                <table className="large-12 ">
                  <thead>
                    <tr>
                      <th>Patient name</th>
                      <th>Account number</th>
                      <th>Creation Date</th>
                      <th>Date Of Birth</th>
                      <th>Insurance</th>
                      <th>Company</th>
                      <th>Branch</th>
                      <th>Status</th>
                      <th>Doctor</th>
                      <th>Doctor Phone Number</th>
                      <th>Onboarding Coach</th>
                      <th>Patient Scheduled</th>
                      <th>Reason</th>
                      <th>Patient Setup</th>
                      <th>Reason</th>
                      <th>Outreach date</th>
                      <th>Latest contact</th>
                    </tr>
                  </thead>
                  <tbody>
                    {patientList.map(patient => (
                      <tr key={patient.PatientGuid}>
                        <td>
                          <span
                            onClick={() => {
                              dispatch(
                                setPatientBatchIds(
                                  [patient.PatientGuid],
                                  [patient.PatientName]
                                )
                              );
                              dispatch(
                                createTab({
                                  type: "patients",
                                  key: "patients",
                                  label: `Patients (${selectedRows.length})`,
                                  meta: { ids: [patient.PatientGuid] }
                                })
                              );
                              clearSelectedRows();
                            }}
                            style={{
                              color: "#4281A6",
                              lineHeight: "inherit",
                              textDecoration: "none",
                              cursor: "pointer"
                            }}
                          >
                            {patient.PatientName}
                          </span>
                        </td>
                        <td>{patient.AccountNumber}</td>
                        <td>{formatDate(patient.CreationDate)}</td>
                        <td>{formatDate(patient.DateOfBirth)}</td>
                        <td>{patient.Insurance}</td>
                        <td>{patient.CompanyName || ""}</td>
                        <td>{patient.BranchName}</td>
                        <td>{patient.Status}</td>
                        <td>{patient.Physician}</td>
                        <td>{formatPhone(patient.DoctorPhoneNumber)}</td>
                        <td>{patient.OnboardingCoach}</td>
                        <td>{patient.PatientScheduled}</td>
                        <td>{getReasonText(patient.ScheduledReason)}</td>
                        <td>{patient.PatientSetup}</td>
                        <td>{getReasonText(patient.SetupReason)}</td>
                        <td>{formatDate(patient.OutreachDate)}</td>
                        <td>{formatDate(patient.LatestContact)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p>No records found for applied filters</p>
              )
            ) : (
              <Spinner />
            )}
          </div>
        </div>
      </div>
    </SelectRowContext.Provider>
  );
};

OnboardingCoachScorecardPatients.propTypes = {
  filters: PropTypes.object.isRequired,
  closeModal: PropTypes.func.isRequired,
  inactivationReasons: PropTypes.arrayOf(PropTypes.object)
};

export default connect(state => ({
  inactivationReasons: selectors.getEnumInactivationReasons(state)
}))(OnboardingCoachScorecardPatients);
